import { useReducer } from 'react';

import { NestedLocation, NestedLocationIndex } from '../../../types/nestedLocations';
import {
  changeAncestorsSelectionStatus,
  clearChildrenSelectStatus,
  convertSelectedLocationsToMap,
  getLocationAncestors,
  unselectLocationAncestors,
} from './LocationsHelpers';

export type SelectedStatus =
  | 'selected'
  | 'excluded'
  | 'onlyChildren'
  /** Group mode: parent kept after "deselect all" children — UI shows indeterminate [-]. */
  | 'partialChildren'
  | undefined;
export type SelectedLocations = Map<number, SelectedStatus>;

export interface LocationsState {
  locationById: NestedLocationIndex;
  childrenByParentId: Map<number, number[]>;
  selectedLocations: SelectedLocations;
  allLocationsSelected: boolean;
}

interface ResetLocationStateAction {
  type: 'reset_location_state';
  payload: Partial<LocationsState>;
}

interface UpdateLocationChildrenAction {
  type: 'update_location_children';
  payload: {
    targetLocationId: number;
    newLocations: NestedLocation[];
    has_more_children: boolean;
  };
}

interface SetLocationExpandedAction {
  type: 'set_location_expanded';
  payload: {
    locationId: number;
  };
}

interface UpdateSignleLocationSelectionAction {
  type: 'update_single_location_selection';
  payload: number;
}

interface UpdateLocationSelectionAction {
  type: 'update_location_selection';
  payload: number;
}

interface ToggleAllLocationsSelectionAction {
  type: 'toggle_all_locations_selection';
}

interface DeleteLocationAction {
  type: 'delete_location';
  payload: {
    locationId: number;
  };
}

type LocationsAction =
  | ResetLocationStateAction
  | UpdateLocationChildrenAction
  | SetLocationExpandedAction
  | UpdateLocationSelectionAction
  | UpdateSignleLocationSelectionAction
  | ToggleAllLocationsSelectionAction
  | DeleteLocationAction;

export const getDefaultLocationsState = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
): LocationsState => ({
  locationById: new Map<number, NestedLocation>(),
  selectedLocations: convertSelectedLocationsToMap(initialSelectedLocations || {}),
  childrenByParentId: new Map<number, number[]>(),
  allLocationsSelected: initialAllLocationsSelected || false,
});

/**
 * Toggles the selection status of a specific location
 * At the same time it change the selection status of the location and its ancestors or parents.
 * It also clear the selection status of all children of the location.
 */
const toggleLocationSelectionAction = (
  state: LocationsState,
  { payload: locationId }: UpdateLocationSelectionAction,
) => {
  const newState = {
    locationById: new Map(state.locationById),
    childrenByParentId: new Map(state.childrenByParentId),
    selectedLocations: new Map(state.selectedLocations),
    allLocationsSelected: state.allLocationsSelected,
  };

  const location = newState.locationById.get(locationId);
  if (!location) return state;

  const ancestors = getLocationAncestors(location.id, newState.locationById);
  const locationSelectedStatus = newState.selectedLocations.get(location.id);

  const atLeastOneAncestorSelected = ancestors.some((ancestor) =>
    ['selected', 'onlyChildren'].includes(newState.selectedLocations.get(ancestor.id)),
  );
  const noAncestorExcluded = ancestors.every(
    (ancestor) => newState.selectedLocations.get(ancestor.id) !== 'excluded',
  );

  const isSelected =
    (!['excluded', 'onlyChildren'].includes(locationSelectedStatus) &&
      atLeastOneAncestorSelected &&
      noAncestorExcluded) ||
    locationSelectedStatus === 'selected' ||
    state.allLocationsSelected;

  clearChildrenSelectStatus(
    location.id,
    newState.locationById,
    newState.childrenByParentId,
    newState.selectedLocations,
  );

  if (isSelected) {
    newState.selectedLocations.set(location.id, 'excluded');
    unselectLocationAncestors(location.id, newState.selectedLocations, newState.locationById);
    newState.allLocationsSelected = false;
  } else {
    newState.selectedLocations.set(location.id, 'selected');
    changeAncestorsSelectionStatus(
      location.parent_id,
      newState.selectedLocations,
      newState.locationById,
      newState.childrenByParentId,
    );
  }

  return newState;
};

/** Append new sublocations to the target location. Also update the target location with the new has_more_children value. */
const updateLocationChildrenAction = (
  state: LocationsState,
  action: UpdateLocationChildrenAction,
) => {
  const locationById = new Map(state.locationById);
  const childrenByParentId = new Map(state.childrenByParentId);

  // Get the target location
  const targetLocation = locationById.get(action.payload.targetLocationId);
  if (!targetLocation) return state;

  // Update the target location with the new has_more_children value
  targetLocation.has_more_children = action.payload.has_more_children;

  // Add new locations to the locationById and the childrenByParentId
  action.payload.newLocations.forEach((newLocation) => {
    locationById.set(newLocation.id, newLocation);
    const currentChildren = childrenByParentId.get(newLocation.parent_id) || [];
    childrenByParentId.set(newLocation.parent_id, [...currentChildren, newLocation.id]);
  });

  return { ...state, locationById, childrenByParentId };
};

/** Toggle the expanded state of a location. */
const setLocationExpandedAction = (state: LocationsState, action: SetLocationExpandedAction) => {
  const locationById = new Map(state.locationById);
  const currentLocation = locationById.get(action.payload.locationId);
  if (currentLocation) {
    currentLocation.expanded = !currentLocation.expanded;
  }
  return { ...state, locationById };
};

const toggleAllLocationsSelectionAction = (state: LocationsState) => {
  const newState = { ...state };
  newState.selectedLocations.clear();
  newState.allLocationsSelected = !newState.allLocationsSelected;
  return newState;
};

/** Remove a location from the state. Also removes it from its parent's children list. */
const deleteLocationAction = (state: LocationsState, action: DeleteLocationAction) => {
  const locationById = new Map(state.locationById);
  const childrenByParentId = new Map(state.childrenByParentId);
  const selectedLocations = new Map(state.selectedLocations);

  const locationToDelete = locationById.get(action.payload.locationId);
  if (!locationToDelete) return state;

  // Remove from locationById
  locationById.delete(action.payload.locationId);

  // Remove from parent's children list
  const parentId = locationToDelete.parent_id;
  if (parentId !== null) {
    const siblings = childrenByParentId.get(parentId) || [];
    const updatedSiblings = siblings.filter((id) => id !== action.payload.locationId);
    childrenByParentId.set(parentId, updatedSiblings);

    // Update the parent's children count if there are no more siblings
    const parent = locationById.get(parentId);
    if (parent && updatedSiblings.length === 0 && !parent?.has_more_children) {
      parent.children_count = 0;
    }
  }

  // Remove from selectedLocations if present
  selectedLocations.delete(action.payload.locationId);

  // Recursively delete all descendants
  const deleteDescendants = (parentId: number) => {
    const childrenIds = childrenByParentId.get(parentId) || [];
    childrenIds.forEach((childId) => {
      deleteDescendants(childId);
      locationById.delete(childId);
      selectedLocations.delete(childId);
    });
    childrenByParentId.delete(parentId);
  };

  deleteDescendants(action.payload.locationId);

  return { ...state, locationById, childrenByParentId, selectedLocations };
};

const locationsReducer = (state: LocationsState, action: LocationsAction) => {
  switch (action.type) {
    case 'reset_location_state':
      return { ...state, ...action.payload };
    case 'set_location_expanded': {
      return setLocationExpandedAction(state, action);
    }
    case 'update_location_children': {
      return updateLocationChildrenAction(state, action);
    }
    case 'update_location_selection': {
      return toggleLocationSelectionAction(state, action);
    }
    case 'update_single_location_selection': {
      const isCurrentlySelected = state.selectedLocations.get(action.payload) === 'selected';

      if (isCurrentlySelected) {
        return {
          ...state,
          selectedLocations: new Map<number, SelectedStatus>(),
        };
      } else {
        return {
          ...state,
          selectedLocations: new Map<number, SelectedStatus>([[action.payload, 'selected']]),
        };
      }
    }
    case 'toggle_all_locations_selection': {
      return toggleAllLocationsSelectionAction(state);
    }
    case 'delete_location': {
      return deleteLocationAction(state, action);
    }
    default:
      return state;
  }
};

export const useLocationsReducer = (
  initialSelectedLocations?: Record<string, SelectedStatus>,
  initialAllLocationsSelected?: boolean,
) => {
  return useReducer(
    locationsReducer,
    getDefaultLocationsState(initialSelectedLocations, initialAllLocationsSelected),
  );
};
